import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Map, Mail, Phone, Clock, Linkedin } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { supabaseLite } from '@/lib/supabase-lite';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { useIsMobile } from "@/hooks/use-mobile";
const serviceOptions = [
  "CRM automation",
  "Responsive email",
  "Front-end development",
  "Custom project"
] as const;

const contactFormSchema = z.object({
  name: z.string().min(2, {
    message: "Name must be at least 2 characters."
  }),
  email: z.string().email({
    message: "Please enter a valid email address."
  }),
  serviceType: z.enum(serviceOptions).optional(),
  subject: z.string().min(2, {
    message: "Subject must be at least 2 characters."
  }),
  message: z.string().min(10, {
    message: "Message must be at least 10 characters."
  })
});
type ContactFormValues = z.infer<typeof contactFormSchema>;
const ContactSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lastSubmissionTime = useRef<number>(0);
  const {
    toast
  } = useToast();
  const isMobile = useIsMobile();
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      serviceType: undefined,
      subject: "",
      message: ""
    }
  });
  const onSubmit = async (data: ContactFormValues) => {
    // Rate limiting: Prevent submissions within 30 seconds
    const now = Date.now();
    const timeSinceLastSubmission = now - lastSubmissionTime.current;
    const minInterval = 30000; // 30 seconds

    if (timeSinceLastSubmission < minInterval) {
      const remainingTime = Math.ceil((minInterval - timeSinceLastSubmission) / 1000);
      toast({
        title: "Please wait",
        description: `Please wait ${remainingTime} seconds before sending another message.`,
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const subjectPrefix = data.serviceType ? `[${data.serviceType}] ` : "";
      const {
        error
      } = await supabaseLite.from('contact_messages').insert({
        name: data.name,
        email: data.email,
        subject: `${subjectPrefix}${data.subject}`,
        message: data.message
      });
      if (error) {
        console.error('Error submitting message:', error);
        toast({
          title: "Message not sent",
          description: "There was an error sending your message. Please try again.",
          variant: "destructive"
        });
      } else {
        // Update last submission time on successful submission
        lastSubmissionTime.current = now;
        
        toast({
          title: "Message sent",
          description: "Thank you! We'll get back to you shortly."
        });

        // Reset form
        form.reset();
      }
    } catch (err) {
      console.error('Error in message submission:', err);
      toast({
        title: "Message not sent",
        description: "An unexpected error occurred. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="contact" className="px-4 relative overflow-hidden bg-gradient-to-b from-blue-50 to-white py-[40px]">
      <div className="container mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Let's Build Something <span className="text-primary">Great Together</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-xl mx-auto">Whether you need CRM automation, responsive email development, front-end solutions, or a custom digital project, I'd love to hear about it.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          {/* Contact Info */}
          <div className="lg:col-span-5">
            <div className="bg-secondary rounded-xl p-8 text-white shadow-lg py-[38px] h-full flex flex-col">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>

              <div className="space-y-6 mb-6">
                <div className="flex items-start space-x-4">
                  <Map className="h-6 w-6 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-white/90 mb-1">Location</h4>
                    <p className="text-white/80">
                      16-ta Makedonska Brigada 4/2, Skopje 1000
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Mail className="h-6 w-6 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-white/90 mb-1">Email Me</h4>
                    <a href="mailto:antonio.dimoski@ad-email-lab.com" className="text-white/80 hover:text-white transition-colors block">
                      antonio.dimoski@ad-email-lab.com
                    </a>
                    <a href="mailto:adimoski994@gmail.com" className="text-white/80 hover:text-white transition-colors block">
                      adimoski994@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Phone className="h-6 w-6 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-white/90 mb-1">Call Me</h4>
                    <a href="tel:+38975280030" className="text-white/80 hover:text-white transition-colors">
                      +389-75-280-030
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Clock className="h-6 w-6 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-white/90 mb-1">Office Hours</h4>
                    <p className="text-white/80">Monday - Friday: 8AM - 8PM</p>
                  </div>
                </div>
              </div>
              
              {/* Profile Card - Mobile Optimized */}
              <Card className="bg-white/10 border-white/20 overflow-hidden">
                <CardContent className={`p-4 ${isMobile ? 'flex flex-col items-center text-center' : 'flex items-center gap-4'}`}>
                  {isMobile ? <>
                      <Avatar className="h-24 w-24 border-2 border-white/20 mb-3">
                        <AvatarImage src="/lovable-uploads/17629760-b890-42ef-b700-9db6705e4368.webp" alt="Antonio Dimoski" />
                        <AvatarFallback>AD</AvatarFallback>
                      </Avatar>
                      <div>
                        <h4 className="text-lg font-bold text-white mb-1">Antonio Dimoski</h4>
                        <p className="text-white/80 text-sm mb-2">CRM & Automation Specialist | Email & Front-End Developer | Digital Marketing & Ads Specialist</p>
                        <a href="https://www.linkedin.com/in/antonio-dimoski/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-white/90 hover:text-white transition-colors text-sm bg-blue-600/30 px-3 py-1 rounded-full">
                          <Linkedin className="h-4 w-4" />
                          <span>Connect on LinkedIn</span>
                        </a>
                      </div>
                    </> : <>
                      <Avatar className="h-16 w-16 border-2 border-white/20">
                        <AvatarImage src="/lovable-uploads/17629760-b890-42ef-b700-9db6705e4368.webp" alt="Antonio Dimoski" />
                        <AvatarFallback>AD</AvatarFallback>
                      </Avatar>
                      <div>
                        <h4 className="text-lg font-bold text-white">Antonio Dimoski</h4>
                        <p className="text-white/80 text-sm">CRM & Automation Specialist | Email & Front-End Developer | Digital Marketing & Ads Specialist</p>
                        <a href="https://www.linkedin.com/in/antonio-dimoski/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-white/80 hover:text-white transition-colors text-sm mt-1">
                          <Linkedin className="h-4 w-4" />
                          <span>Connect on LinkedIn</span>
                        </a>
                      </div>
                    </>}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-7">
            <div className="bg-white rounded-xl shadow-lg p-8 h-full flex flex-col">
              <h3 className="text-2xl font-bold mb-6 text-secondary">Send Me a Message</h3>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField control={form.control} name="name" render={({
                    field
                  }) => <FormItem>
                          <FormLabel>Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Your name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                    <FormField control={form.control} name="email" render={({
                    field
                  }) => <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="Your email" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                  </div>

                  <FormField control={form.control} name="serviceType" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Choose a start option</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="What do you need help with?" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {serviceOptions.map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="subject" render={({
                  field
                }) => <FormItem>
                        <FormLabel>Subject</FormLabel>
                        <FormControl>
                          <Input placeholder="What is this regarding?" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />

                  <FormField control={form.control} name="message" render={({
                  field
                }) => <FormItem>
                        <FormLabel>Message</FormLabel>
                        <FormControl>
                          <Textarea placeholder="Your message" className="w-full min-h-[150px]" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />

                  <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-white" disabled={isSubmitting}>
                    {isSubmitting ? <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending Message...
                      </span> : "Send Message"}
                  </Button>
                </form>
              </Form>
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="mt-16 rounded-xl overflow-hidden shadow-lg h-[400px] border border-gray-200">
          <iframe title="Location Map" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2965.2801799542037!2d21.414779415701086!3d41.995522079214994!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x135415571abc4aed%3A0x3e659c8f55bf748a!2s16-ta%20Makedonska%20Brigada%204%2F2%2C%20Skopje!5e0!3m2!1sen!2smk!4v1647093361863!5m2!1sen!2smk" width="100%" height="100%" style={{
          border: 0
        }} allowFullScreen loading="lazy"></iframe>
        </div>
      </div>
    </section>;
};
export default ContactSection;